import type {
    CaptureOptionsBySource,
    ToggleSource,
} from "@livekit/components-core"
import * as React from "react"
import { Track, TrackPublishOptions } from "livekit-client"
import { useTrackToggle } from "@livekit/components-react"
import { theme } from "../../theme/theme"
import {
    CameraDisabledIcon,
    CameraIcon,
    MicDisabledIcon,
    MicIcon,
    ScreenShareIcon,
    ScreenShareStopIcon,
} from "@livekit/components-react/src/assets/icons"
import FontAwesome6 from "react-native-vector-icons/FontAwesome6"

export const liveButtonStyle = {
    backgroundColor: theme.colors.lighter,
    border: "none",
    padding: 10,
    borderRadius: 20,
    color: theme.colors.primary,
    fontSize: 24,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 50,
    height: 44,
    cursor: "pointer",
    transition: "background-color 0.3s ease",
}

function getSourceIcon(source: Track.Source, enabled: boolean) {
    switch (source) {
        case Track.Source.Microphone:
            return enabled ? (
                <FontAwesome6 name={"microphone"} size={20} color={"white"} />
            ) : (
                <FontAwesome6
                    name={"microphone-slash"}
                    size={20}
                    color={theme.colors.primary}
                />
            )
        case Track.Source.Camera:
            return enabled ? (
                <FontAwesome6 name={"video"} size={20} color={"white"} />
            ) : (
                <FontAwesome6
                    name={"video-slash"}
                    size={20}
                    color={theme.colors.primary}
                />
            )
        case Track.Source.ScreenShare:
            return enabled ? (
                <FontAwesome6
                    name={"rectangle-xmark"}
                    size={20}
                    color={"white"}
                />
            ) : (
                <FontAwesome6
                    name={"display"}
                    size={20}
                    color={theme.colors.primary}
                />
            )
        default:
            return undefined
    }
}

/** @public */
export interface TrackToggleProps<T extends ToggleSource>
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
    source: T
    showIcon?: boolean
    initialState?: boolean
    // miniMode?: boolean;
    /**
     * Function that is called when the enabled state of the toggle changes.
     * The second function argument `isUserInitiated` is `true` if the change was initiated by a user interaction, such as a click.
     */
    onChange?: (enabled: boolean, isUserInitiated: boolean) => void
    captureOptions?: CaptureOptionsBySource<T>
    publishOptions?: TrackPublishOptions
    onDeviceError?: (error: Error) => void
}

/**
 * With the `TrackToggle` component it is possible to mute and unmute your camera and microphone.
 * The component uses an html button element under the hood so you can treat it like a button.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <TrackToggle source={Track.Source.Microphone} />
 *   <TrackToggle source={Track.Source.Camera} />
 * </LiveKitRoom>
 * ```
 * @public
 */
export const TrackToggleM: <T extends ToggleSource>(
    props: TrackToggleProps<T> & React.RefAttributes<HTMLButtonElement>
) => React.ReactNode = /* @__PURE__ */ React.forwardRef(function TrackToggle<
    T extends ToggleSource
>(
    { showIcon, ...props }: TrackToggleProps<T>,
    ref: React.ForwardedRef<HTMLButtonElement>
) {
    const { buttonProps, enabled } = useTrackToggle(props)

    return (
        <button
            ref={ref}
            {...buttonProps}
            style={{
                ...liveButtonStyle,
                backgroundColor: enabled
                    ? theme.colors.primary
                    : theme.colors.lighter,
            }}
            disabled={props.disabled}
        >
            {(showIcon ?? true) && getSourceIcon(props.source, enabled)}
            {props.children}
        </button>
    )
})
